import { CheckCircle2 } from "lucide-react";
import { StatusBadge } from "@/components/common/StatusBadge";
import { mxn, fdate } from "@/lib/format";

const round2 = (n) => Math.round(Number(n || 0) * 100) / 100;

// Cuota de crédito por renglón (capital + interés, SIN IVA) — leída de la tabla SELLADA.
export const cuotaDeRenglon = (c) =>
  c.cuota_credito ?? Math.round((Number(c.capital || 0) + Number(c.interes || 0)) * 100) / 100;

// Mora POR CUOTA tal como la dejó el motor (pagos_engine) — NUNCA se recalcula aquí:
//  - "diferida":  cuota pagada tarde con moratorio CONGELADO pendiente (mora_diferida −
//                 mora_diferida_pagada) + días congelados (mora_congelada_dias).
//  - "devengada": cuota vencida impaga (mora_acumulada − pagado_moratorio) + dias_atraso.
//  - "liquidada": moratorio ya cubierto (pagado_moratorio o diferida saldada).
//  - null: sin mora.
export const moraDeRenglon = (c) => {
  if (c.mora_congelada) {
    const monto = round2(c.mora_diferida);
    const pend = Math.max(0, round2(monto - round2(c.mora_diferida_pagada)));
    if (pend > 0) return { tipo: "diferida", monto: pend, dias: c.mora_congelada_dias };
    const liq = monto || round2(c.pagado_moratorio);
    return liq > 0 ? { tipo: "liquidada", monto: liq, dias: c.mora_congelada_dias } : null;
  }
  const dev = Math.max(0, round2(round2(c.mora_acumulada) - round2(c.pagado_moratorio)));
  if (dev > 0) return { tipo: "devengada", monto: dev, dias: c.dias_atraso };
  if (round2(c.pagado_moratorio) > 0)
    return { tipo: "liquidada", monto: round2(c.pagado_moratorio), dias: c.dias_atraso };
  return null;
};

// IVA del moratorio POR CUOTA (Bloque 1) — mismos estados que la mora, leídos de los
// campos SELLADOS por el motor (iva_mora_acumulada / mora_diferida_iva / pagados).
export const ivaMoraDeRenglon = (c) => {
  if (c.mora_congelada) {
    const pend = Math.max(0, round2(round2(c.mora_diferida_iva) - round2(c.mora_diferida_iva_pagada)));
    if (pend > 0) return { tipo: "diferida", monto: pend };
    const liq = round2(c.mora_diferida_iva_pagada) || round2(c.pagado_iva_moratorio);
    return liq > 0 ? { tipo: "liquidada", monto: liq } : null;
  }
  const dev = Math.max(0, round2(round2(c.iva_mora_acumulada) - round2(c.pagado_iva_moratorio)));
  if (dev > 0) return { tipo: "devengada", monto: dev };
  if (round2(c.pagado_iva_moratorio) > 0)
    return { tipo: "liquidada", monto: round2(c.pagado_iva_moratorio) };
  return null;
};

// Capital PENDIENTE real de la cuota = capital sellado − capital pagado. El motor cobra
// la mora del siguiente periodo sobre ESTE pendiente (pagos_engine rem["capital"]) — el
// capital sellado NUNCA aumenta (sin capitalización, Art. 363 CCom).
export const capitalPendienteDeRenglon = (c) =>
  Math.max(0, round2(round2(c.capital) - round2(c.pagado_capital)));

// Agrupa los cargos de GASTOS DE COBRANZA (cargos_cobranza del crédito) por cuota_no.
// Cargos sin cuota (manuales viejos) se agregan bajo la llave "_credito".
const gcPorCuota = (cargos) => {
  const map = {};
  (cargos || []).forEach((g) => {
    if (g.revertido) return;
    const key = g.cuota_no ?? "_credito";
    if (!map[key]) map[key] = { monto: 0, iva: 0, pagadoMonto: 0, pagadoIva: 0 };
    map[key].monto = round2(map[key].monto + Math.max(0, round2(g.monto) - round2(g.pagado_monto)));
    map[key].iva = round2(map[key].iva + Math.max(0, round2(g.iva) - round2(g.pagado_iva)));
    map[key].pagadoMonto = round2(map[key].pagadoMonto + round2(g.pagado_monto));
    map[key].pagadoIva = round2(map[key].pagadoIva + round2(g.pagado_iva));
  });
  return map;
};

function MoraCell({ c }) {
  const m = moraDeRenglon(c);
  if (!m) return <>—</>;
  if (m.tipo === "devengada")
    return (
      <span className="text-red-600" data-testid={`mora-devengada-${c.no}`}
        title={`Mora devengada — ${m.dias || 0} días de atraso`}>
        {mxn(m.monto)}
        {Number(m.dias) > 0 && <span className="block text-[9px] font-sans leading-tight text-red-400">{m.dias} {Number(m.dias) === 1 ? "día" : "días"}</span>}
      </span>
    );
  if (m.tipo === "diferida")
    return (
      <span className="text-amber-600" data-testid={`mora-diferida-${c.no}`}
        title={`Moratorio diferido pendiente — la cuota se pagó con ${m.dias || 0} días de atraso`}>
        {mxn(m.monto)}
        <span className="block text-[9px] font-sans leading-tight text-amber-500">{m.dias || 0} {Number(m.dias) === 1 ? "día" : "días"} · diferida</span>
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 text-slate-400" data-testid={`mora-liquidada-${c.no}`}
      title="Moratorio ya liquidado — no es deuda">
      <CheckCircle2 className="h-3 w-3 shrink-0 text-emerald-500" />{mxn(m.monto)}
    </span>
  );
}

function IvaMoraCell({ c }) {
  const m = ivaMoraDeRenglon(c);
  if (!m) return <>—</>;
  if (m.tipo === "devengada")
    return <span className="text-red-600" data-testid={`iva-mora-devengada-${c.no}`} title="IVA del moratorio devengado (sellado por el motor)">{mxn(m.monto)}</span>;
  if (m.tipo === "diferida")
    return <span className="text-amber-600" data-testid={`iva-mora-diferida-${c.no}`} title="IVA del moratorio diferido pendiente">{mxn(m.monto)}</span>;
  return (
    <span className="inline-flex items-center gap-1 text-slate-400" data-testid={`iva-mora-liquidada-${c.no}`} title="IVA del moratorio ya liquidado">
      <CheckCircle2 className="h-3 w-3 shrink-0 text-emerald-500" />{mxn(m.monto)}
    </span>
  );
}

function GcCell({ gc, no, iva = false, etiqueta = "Gastos de cobranza" }) {
  if (!gc) return <>—</>;
  const pend = iva ? gc.iva : gc.monto;
  const pagado = iva ? gc.pagadoIva : gc.pagadoMonto;
  if (pend > 0)
    return <span className="text-red-600" data-testid={`${iva ? "iva-gc" : "gc"}-pendiente-${no}`} title={iva ? `IVA de "${etiqueta}" pendiente (el cargo siempre causa IVA)` : `${etiqueta} pendiente de esta cuota`}>{mxn(pend)}</span>;
  if (pagado > 0)
    return (
      <span className="inline-flex items-center gap-1 text-slate-400" data-testid={`${iva ? "iva-gc" : "gc"}-liquidado-${no}`} title="Ya liquidado">
        <CheckCircle2 className="h-3 w-3 shrink-0 text-emerald-500" />{mxn(pagado)}
      </span>
    );
  return <>—</>;
}

/** Tabla de amortización compartida (Cartera modo lectura; modal de Pagos con `selectable`).
 *  Los importes por renglón se muestran tal como están SELLADOS por el motor — nunca se
 *  recalcula nada aquí. Columnas condicionales (se ocultan si todo es $0):
 *  - "IVA mora": IVA del moratorio por cuota (créditos gravados con mora).
 *  - "GC" / "IVA GC": gastos de cobranza por cuota (prop cargosGc = cargos_cobranza).
 *  - Capital: si hay pago parcial, muestra el PENDIENTE real (sellado − pagado). */
export function TablaAmortizacionCredito({ tabla = [], cargosGc = [], gcEtiqueta = "Gastos de cobranza", selectable = false, selectedNo = null, onSelectCuota, testId, resaltarTotal = false }) {
  const gcMap = gcPorCuota(cargosGc);
  const showIvaMora = tabla.some((c) => ivaMoraDeRenglon(c));
  const showGc = Object.keys(gcMap).some((k) => k !== "_credito");
  const gcCredito = gcMap._credito;
  return (
    <div className="max-h-64 overflow-auto rounded-lg border border-slate-200" data-testid={testId}>
      <table className="w-full text-left text-xs">
        <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-2 py-2">#</th><th className="px-2 py-2">Vence</th>
            <th className="px-2 py-2 text-right">Capital</th><th className="px-2 py-2 text-right">Interés</th>
            <th className="px-2 py-2 text-right">IVA</th>
            <th className="px-2 py-2 text-right" title="Capital + interés — constante en sistema francés">Cuota</th>
            <th className="px-2 py-2 text-right" title="TOTAL EXIGIBLE por el motor de pagos: cuota + IVA + comisión + IVA comisión + mora corriente + IVA mora + mora congelada diferida + IVA + gastos de cobranza activos + IVA + gastos de cobranza diferidos + IVA. Los cargos a nivel crédito se atribuyen a la próxima cuota exigible.">Total exigible</th>
            <th className="px-2 py-2 text-right">Mora</th>
            {showIvaMora && <th className="px-2 py-2 text-right" title="IVA del interés moratorio — solo créditos gravados (Art. 15-X LIVA)">IVA mora</th>}
            {showGc && <th className="px-2 py-2 text-right" title="Gastos de cobranza cargados a esta cuota">GC</th>}
            {showGc && <th className="px-2 py-2 text-right" title="IVA de los gastos de cobranza — siempre aplica (los GC no son intereses)">IVA GC</th>}
            <th className="px-2 py-2 text-right">Pagado</th>
            <th className="px-2 py-2">Estatus</th>
          </tr>
        </thead>
        <tbody>
          {tabla.map((c) => {
            const clickable = selectable && c.estatus !== "Pagada";
            const sel = selectable && selectedNo != null && c.no === selectedNo;
            const capPend = capitalPendienteDeRenglon(c);
            const parcialCapital = c.estatus !== "Pagada" && round2(c.pagado_capital) > 0 && capPend > 0;
            return (
              <tr key={c.no}
                className={`border-t border-slate-100${clickable ? " cursor-pointer hover:bg-sky-50/60" : ""}${sel ? " bg-sky-50" : ""}`}
                aria-selected={sel || undefined}
                data-testid={`cuota-row-${c.no}`}
                onClick={clickable ? () => onSelectCuota?.(c) : undefined}>
                <td className="px-2 py-1.5 font-mono text-slate-500">{c.no}</td>
                <td className="px-2 py-1.5 text-slate-500">{fdate(c.fecha_pago)}</td>
                <td className="px-2 py-1.5 text-right font-mono">
                  {mxn(c.capital)}
                  {parcialCapital && (
                    <span className="block text-[9px] font-sans leading-tight text-amber-600" data-testid={`capital-pendiente-${c.no}`}
                      title="Capital pendiente REAL (sellado − pagado). La mora del siguiente periodo corre sobre este pendiente — el capital nunca se incrementa.">
                      pendiente {mxn(capPend)}
                    </span>
                  )}
                </td>
                <td className="px-2 py-1.5 text-right font-mono">{mxn(c.interes)}</td>
                <td className="px-2 py-1.5 text-right font-mono">{mxn(c.iva_interes)}</td>
                <td className={`px-2 py-1.5 text-right font-mono${resaltarTotal ? "" : " font-semibold"}`}>{mxn(cuotaDeRenglon(c))}</td>
                <td className={`px-2 py-1.5 text-right font-mono${resaltarTotal ? " font-semibold" : ""}`} data-testid={`cuota-total-exigible-${c.no}`}>
                  {mxn(c.total_exigible != null ? c.total_exigible : (c.total_periodo ?? c.pago))}
                  {c.total_exigible_desglose?.credit_level_incluido && (
                    <span className="ml-1 rounded bg-rose-100 px-1 py-0.5 text-[9px] font-sans font-semibold text-rose-700"
                      title="Incluye cargos a nivel crédito: gastos de cobranza (activos + diferidos + IVA) y/o mora congelada diferida (+ IVA)">
                      + cargos
                    </span>
                  )}
                </td>
                <td className="px-2 py-1.5 text-right font-mono"><MoraCell c={c} /></td>
                {showIvaMora && <td className="px-2 py-1.5 text-right font-mono"><IvaMoraCell c={c} /></td>}
                {showGc && <td className="px-2 py-1.5 text-right font-mono"><GcCell gc={gcMap[c.no]} no={c.no} etiqueta={gcEtiqueta} /></td>}
                {showGc && <td className="px-2 py-1.5 text-right font-mono"><GcCell gc={gcMap[c.no]} no={c.no} etiqueta={gcEtiqueta} iva /></td>}
                <td className="px-2 py-1.5 text-right font-mono text-slate-500">{mxn(c.pagado_acumulado || 0)}</td>
                <td className="px-2 py-1.5"><StatusBadge value={c.estatus || "Pendiente"} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {gcCredito && (gcCredito.monto + gcCredito.iva) > 0 && (
        <p className="border-t border-slate-100 bg-rose-50/50 px-2 py-1.5 text-[10.5px] text-rose-700" data-testid="gc-sin-cuota">
          {gcEtiqueta} del crédito (sin cuota asignada): {mxn(gcCredito.monto)}
          {gcCredito.iva > 0 ? ` + IVA ${mxn(gcCredito.iva)}` : ""} pendientes.
        </p>
      )}
    </div>
  );
}
